import React from "react";
import { Topic, P, H3, UL, Callout, Table, VisualBox } from "@/pages/knowledge/Atoms";

export default function Indices({ Sparkline }) {
  const card = (title, subtitle, data, color, body) => (
    <VisualBox title={title.toUpperCase()}>
      <div className="text-xs text-[#475569] mb-2">{subtitle}</div>
      <Sparkline data={data} color={color} />
      <p className="text-sm text-[#334155] mt-3 leading-relaxed">{body}</p>
    </VisualBox>
  );

  return (
    <>
      <Topic id="nifty-50" title="What is Nifty 50?">
        <P>
          Nifty 50 is the benchmark index of the National Stock Exchange (NSE).
          It represents the top 50 largest and most actively traded companies in
          India — across banking, IT, energy, FMCG and pharma. Think of it as
          the <b>report card of the Indian economy</b>.
        </P>

        <H3>Composition</H3>
        <Table
          headers={["Sector", "Examples"]}
          rows={[
            ["Banking", "HDFC Bank, ICICI Bank"],
            ["IT", "Infosys, TCS, Wipro"],
            ["Energy", "Reliance Industries, ONGC"],
            ["FMCG", "HUL, ITC, Nestle"],
            ["Automobiles", "Tata Motors, Mahindra & Mahindra"],
          ]}
        />

        <P>
          Together these companies cover ~65% of India&rsquo;s total stock-market
          value.
        </P>

        {card(
          "Nifty 50 — illustrative trajectory",
          "Long-term uptrend with corrections",
          [16, 17, 18, 22, 21, 24, 25, 28, 27, 30, 32, 34, 36, 35, 38],
          "#084392",
          "If Nifty rises from 26,000 to 26,200, the top 50 companies have gained ~0.8% on average."
        )}

        <H3>Why it matters</H3>
        <UL>
          <li>Barometer of Indian market sentiment</li>
          <li>Benchmark for mutual funds &amp; portfolio performance</li>
          <li>Base for index funds, ETFs and derivatives (F&amp;O)</li>
          <li>Helps analyse sector trends and risk</li>
        </UL>

        <Table
          headers={["Key point", "Description"]}
          rows={[
            ["Exchange", "NSE"],
            ["No. of stocks", "50"],
            ["Lot size (Dec 2025)", "75"],
            ["Weekly expiry", "Every Tuesday"],
            ["Launched", "1996"],
          ]}
        />
      </Topic>

      <Topic id="bank-nifty" title="What is Bank Nifty?">
        <P>
          Bank Nifty (Nifty Bank) represents the 12 most liquid banking stocks
          on the NSE. If Nifty 50 is India&rsquo;s overall health report, Bank
          Nifty is the <b>heartbeat of the financial sector</b>.
        </P>
        <H3>Composition</H3>
        <Table
          headers={["Type", "Banks"]}
          rows={[
            ["Private", "HDFC Bank, ICICI, Axis, Kotak Mahindra, IndusInd, Federal, Bandhan"],
            ["Public", "SBI"],
          ]}
        />
        {card(
          "Bank Nifty — illustrative",
          "Often leads market rallies during rate cuts & strong credit growth",
          [44, 45, 46, 45, 47, 48, 47, 49, 50, 51, 52, 51, 53],
          "#10b981",
          "If Bank Nifty rises from 56,000 to 56,500, banking stocks have gained roughly 0.9% on average."
        )}
        <Table
          headers={["Key point", "Description"]}
          rows={[
            ["Exchange", "NSE"],
            ["No. of stocks", "12"],
            ["Lot size (Dec 2025)", "30"],
            ["Expiry", "Monthly — last Tuesday"],
            ["Sector", "Banking"],
          ]}
        />
        <Callout kind="info">
          Bank Nifty weekly expiry was discontinued in late 2024. Only monthly
          expiry trades now.
        </Callout>
      </Topic>

      <Topic id="sensex" title="What is Sensex?">
        <P>
          The Sensex (Sensitive Index) is the benchmark index of the Bombay
          Stock Exchange (BSE). It represents the top 30 well-established and
          financially strong companies on the BSE. Nifty 50 belongs to NSE;
          Sensex belongs to BSE.
        </P>
        <Table
          headers={["Sector", "Examples"]}
          rows={[
            ["Banking", "HDFC Bank, ICICI Bank"],
            ["IT", "Infosys, TCS"],
            ["Energy", "Reliance Industries"],
            ["FMCG", "HUL, ITC"],
            ["Auto", "Maruti Suzuki, Tata Motors"],
          ]}
        />
        {card(
          "Sensex — illustrative",
          "India's oldest stock-market index (started 1986)",
          [62, 63, 64, 66, 67, 70, 72, 74, 75, 77, 79, 80, 82],
          "#084392",
          "If Sensex moves from 86,000 to 87,000, the top 30 companies have gained ~1.2% on average."
        )}
        <Table
          headers={["Key point", "Description"]}
          rows={[
            ["Exchange", "BSE"],
            ["No. of stocks", "30"],
            ["Lot size (Dec 2025)", "20"],
            ["Weekly expiry", "Every Tuesday"],
            ["Launched", "1986"],
          ]}
        />
      </Topic>

      <Topic id="bankex" title="What is Bankex?">
        <P>
          Bankex is the Banking Index of the BSE — similar to how Bank Nifty
          works for NSE. It tracks major banking companies listed on the BSE,
          both public and private.
        </P>
        <Table
          headers={["Type", "Banks"]}
          rows={[
            ["Private", "HDFC Bank, ICICI, Axis, Kotak Mahindra, IndusInd, Federal"],
            ["Public", "SBI"],
          ]}
        />
        {card(
          "Bankex — illustrative",
          "BSE banking sector",
          [50, 51, 53, 52, 54, 55, 56, 57, 58, 60, 59, 61],
          "#10b981",
          "Bankex moving from 53,000 to 54,000 means BSE banks have, on average, risen ~1.9%."
        )}
        <Table
          headers={["Key point", "Description"]}
          rows={[
            ["Exchange", "BSE"],
            ["No. of stocks", "~10–12"],
            ["Sector", "Banking"],
            ["Comparable Index", "NSE Bank Nifty"],
          ]}
        />
      </Topic>
    </>
  );
}
